import { useCallback, useEffect, useMemo, useState } from "react";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { Accordion, AccordionTab } from "primereact/accordion";
import { AnimatedCheckbox } from "app/uicomponents/microcomponents/AnimatedChecbox";
import { type TableDataRow, getPerformanceTableColumns } from "./performanceChartModel";
import PerformanceTablePanel from "./PerformanceTablePanel";
import PerformanceChartPanel, { type PerformanceAssetLineChartData } from "./PerformanceChartPanel";
import InterestCreditsPanel from "./InterestCreditsPanel";

// ── API filter ids (must match backend) ───────────────────────────────────────

const PERFORMANCE_API_FILTERS = [
  "ACCOUNT_VALUE",
  "SURRENDER_VALUE",
  "DEATH_BENEFIT",
  "INCOME",
  "FINANCING",
] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

type ValueMetricId = "accountValue" | "surrenderValue" | "deathBenefit" | "income" | "financing";

interface MetricBlock {
  projected: number | null;
  actual: number | null;
  trueIQIndex?: number | null;
}

interface IncomeBlock {
  annualIncome: number;
  totalIncome: number;
}

interface GraphDataRow {
  planYear: number;
  year: number;
  insuredAge: number;
  interestCredits: number;
  accountValue: MetricBlock;
  align: MetricBlock;
  surrenderValue: MetricBlock;
  deathBenefit: MetricBlock;
  income: IncomeBlock;
  financing: MetricBlock;
}

interface PerformanceApiData {
  graphData: GraphDataRow[];
  tableData?: TableDataRow[];
}

interface InsuredOption {
  id: string;
  name: string;
}

const VALUE_METRICS: { id: ValueMetricId; label: string; color: string }[] = [
  { id: "accountValue", label: "Account Value", color: "#93C5FD" },
  { id: "surrenderValue", label: "Surrender Value", color: "#3B82F6" },
  { id: "deathBenefit", label: "Death Benefit", color: "#22D3EE" },
  { id: "income", label: "Income", color: "#9CA3AF" },
  { id: "financing", label: "Financing", color: "#1E3A8A" },
];

const PRIMARY = "#1E3A8A";

// ── Accordion icons ───────────────────────────────────────────────────────────

const accordionHeader = (title: string) => (
  <div className="text-lg conthrax-font ml-2 text-color-black">{title}</div>
);

const collapseIcon = (
  <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
    <GoTriangleDown
      className="text-[#8A8AA3]/70"
      size={27}
      style={{ transition: "transform 0.2s" }}
    />
  </div>
);

const expandIcon = (
  <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
    <GoTriangleUp
      className="text-[#8A8AA3]/70"
      size={27}
      style={{ transition: "transform 0.2s", rotate: "140deg" }}
    />
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

function Performance() {
  const isDark = getSessionStore("globalTheme") === THEME.DARK;
  const planId = getSessionStore("planInfo")?.planId;

  const proposalId = planId;

  const [insuredOptions, setInsuredOptions] = useState<InsuredOption[]>([]);
  const [insuredLoading, setInsuredLoading] = useState(false);

  useEffect(() => {
    if (!planId) return;
    setInsuredLoading(true);
    makeRequest("reviewCenterReview")
      .get(planId, true)
      .then((res: any) => {
        const list: any[] = res?.data?.data?.insuredList ?? [];
        setInsuredOptions(
          list.map((item: any) => ({
            id: item.userId,
            name: `${item.firstName ?? ""} ${item.lastName ?? ""}`.trim() || "Insured",
          })),
        );
      })
      .catch(() => {})
      .finally(() => setInsuredLoading(false));
  }, [planId]);

  const [selectedInsuredId, setSelectedInsuredId] = useState<string>("");

  const [activeValueFilters, setActiveValueFilters] = useState<Set<ValueMetricId>>(
    new Set(VALUE_METRICS.map((m) => m.id)),
  );
  const toggleValueFilter = (id: ValueMetricId) => {
    setActiveValueFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const [trueIqIndexOn, setTrueIqIndexOn] = useState(true);
  const [trueIqAlign, setTrueIqAlign] = useState(false);

  const [assetChartFilter, setAssetChartFilter] = useState<string>("year");
  const [projectionIconActive, setProjectionIconActive] = useState(false);

  const [selectedYear, setSelectedYear] = useState<number>(getSessionStore("ircSelectedYear") ?? 1);
  const [creditType, setCreditType] = useState<string>(
    getSessionStore("ircSelectedCreditType") ?? "preCredit",
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail ?? {};
      const newYear =
        detail.year !== undefined
          ? detail.year
          : getSessionStore("ircSelectedYear") ?? selectedYear;
      const newCreditType =
        detail.creditType !== undefined
          ? detail.creditType
          : getSessionStore("ircSelectedCreditType") ?? creditType;
      console.log("ircSelectionChanged →", { year: newYear, creditType: newCreditType });
      setSelectedYear(newYear);
      setCreditType(newCreditType);
    };
    window.addEventListener("ircSelectionChanged", handler);
    return () => window.removeEventListener("ircSelectionChanged", handler);
  }, []);

  const [graphData, setGraphData] = useState<GraphDataRow[]>([]);
  const [tableData, setTableData] = useState<TableDataRow[]>([]);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [performanceError, setPerformanceError] = useState<string | null>(null);

  useEffect(() => {
    if (!insuredOptions.length || selectedInsuredId) return;
    setSelectedInsuredId(insuredOptions[0].id);
  }, [insuredOptions, selectedInsuredId]);

  const fetchPerformance = useCallback(async () => {
    if (!selectedInsuredId) return;
    setPerformanceLoading(true);
    setPerformanceError(null);
    const filtersParam = encodeURIComponent(JSON.stringify([...PERFORMANCE_API_FILTERS]));
    const insuredIdsParam = encodeURIComponent(JSON.stringify([selectedInsuredId]));
    const query = `filters=${filtersParam}&year=${selectedYear}&insuredIds=${insuredIdsParam}&creditType=${creditType}`;
    try {
      const res = await makeRequest("interactiveReviewCenterPerformance").get(
        `${proposalId}?${query}`,
        true,
      );
      const data = res?.data?.data as PerformanceApiData | undefined;
      setGraphData(Array.isArray(data?.graphData) ? data!.graphData : []);
      setTableData(Array.isArray(data?.tableData) ? data!.tableData! : []);
    } catch (e: unknown) {
      const msg = (e as { message?: string })?.message ?? "Failed to load performance data";
      setPerformanceError(msg);
      setGraphData([]);
    } finally {
      setPerformanceLoading(false);
    }
  }, [proposalId, selectedYear, creditType, selectedInsuredId]);

  useEffect(() => {
    fetchPerformance();
  }, [fetchPerformance]);

  const handleProjectionToggle = useCallback(() => {
    setProjectionIconActive((v) => !v);
  }, []);

  const showProjectedSeries = !projectionIconActive;
  const trueIqShadingOn = trueIqIndexOn || trueIqAlign;

  const insuredName = insuredOptions.find((o) => o.id === selectedInsuredId)?.name ?? "Insured";

  const cardBg = isDark ? "bg-[#2A2A3D]" : "bg-white";
  const divider = isDark ? "border-[#3A3A50]" : "border-gray-100";
  const textMain = isDark ? "text-gray-100" : "text-gray-900";
  const textSub = isDark ? "text-gray-400" : "text-gray-500";

  const projectionsIconFill = projectionIconActive
    ? isDark
      ? "#4A90E2"
      : "#004781"
    : isDark
    ? "#6B7280"
    : "#9CA3AF";

  const chartBusy = insuredLoading || performanceLoading;

  const assetLineChartData = useMemo<PerformanceAssetLineChartData>(
    () =>
      graphData.map((row) => ({
        year: row.year,
        planYear: row.planYear,
        insuredAge: row.insuredAge,
        accountValue: row.accountValue.actual,
        projectedAccountValue: row.accountValue.projected,
        surrenderValue: row.surrenderValue.actual,
        projectedSurrenderValue: row.surrenderValue.projected,
        benefit: row.deathBenefit.actual,
        projectedBenefit: row.deathBenefit.projected,
        financing: row.financing.actual,
        projectedFinancing: row.financing.projected,
      })),
    [graphData],
  );

  const chartSeriesFilters = useMemo(() => [...activeValueFilters], [activeValueFilters]);

  const tableColumns = useMemo(() => getPerformanceTableColumns(), []);

  const tableRowClassName = useCallback(
    (rowData: TableDataRow): string => (rowData.planYear === selectedYear ? "font-semibold" : ""),
    [selectedYear],
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-5 pb-8">
      {!proposalId && (
        <p className={`text-sm ${textSub}`}>
          No proposal is selected. Open this page from a proposal.
        </p>
      )}

      {proposalId && !insuredOptions.length && !insuredLoading && (
        <p className={`text-sm ${textSub}`}>
          No insured persons found on the proposal summary. Insured selection is required for the
          performance API.
        </p>
      )}

      {performanceError && (
        <p className="text-sm text-red-500" role="alert">
          {performanceError}
        </p>
      )}

      {/* ── Insured Person(s) + TrueIQ ──────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p
            className={`text-[18px] conthrax-font font-semibold uppercase tracking-wider mb-2 ${textSub}`}
          >
            Insured Person(s)
          </p>
          <div className="flex items-center gap-3 flex-wrap">
            {insuredOptions.map((person) => (
              <AnimatedCheckbox
                key={person.id}
                checked={selectedInsuredId === person.id}
                onChange={() => setSelectedInsuredId(person.id)}
                inputId={`insured-${person.id}`}
                label={person.name}
                brandingColor={PRIMARY}
                size={20}
              />
            ))}
          </div>
        </div>

        {/* TrueIQ */}
        <div className="flex flex-col items-start gap-2">
          <p
            className={`text-[18px] conthrax-font font-semibold uppercase tracking-wider ${textSub}`}
          >
            TrueIQ
          </p>
          <div className="flex items-center gap-3">
            <AnimatedCheckbox
              checked={trueIqIndexOn}
              onChange={() => setTrueIqIndexOn((v) => !v)}
              inputId="trueiq-index"
              label="Index"
              brandingColor={PRIMARY}
              size={20}
            />
            <AnimatedCheckbox
              checked={trueIqAlign}
              onChange={() => setTrueIqAlign((v) => !v)}
              inputId="trueiq-align"
              label="Align"
              brandingColor={PRIMARY}
              size={20}
            />
          </div>
        </div>
      </div>

      {/* ── Values ──────────────────────────────────────────────────────── */}
      <div>
        <p
          className={`text-[18px] font-semibold uppercase conthrax-font tracking-wider mb-2 ${textSub}`}
        >
          Values
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {VALUE_METRICS.map((m) => (
            <AnimatedCheckbox
              key={m.id}
              checked={activeValueFilters.has(m.id)}
              onChange={() => toggleValueFilter(m.id)}
              inputId={`value-${m.id}`}
              label={
                <span className="flex items-center gap-2">
                  {m.label}
                  <span
                    className="w-[12px] h-[12px] rounded-full inline-block"
                    style={{ backgroundColor: m.color }}
                  />
                </span>
              }
              brandingColor={m.color}
              size={20}
            />
          ))}
        </div>
      </div>

      {/* ── Accordion ───────────────────────────────────────────────────── */}
      <Accordion
        collapseIcon={collapseIcon}
        expandIcon={expandIcon}
        multiple
        activeIndex={[0, 1, 2]}
        className="bg-gray-100 dark:!bg-[#111111] rounded-md ip-project-accordion-main"
      >
        <AccordionTab
          headerTemplate={accordionHeader("Performance Chart")}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          <PerformanceChartPanel
            isDark={isDark}
            cardBg={cardBg}
            divider={divider}
            textMain={textMain}
            textSub={textSub}
            insuredName={insuredName}
            chartBusy={chartBusy}
            graphDataLength={graphData.length}
            performanceLoading={performanceLoading}
            assetLineChartData={assetLineChartData}
            assetChartFilter={assetChartFilter}
            onAssetChartFilterChange={setAssetChartFilter}
            chartSeriesFilters={chartSeriesFilters}
            showProjections={showProjectedSeries}
            onProjectionsToggle={handleProjectionToggle}
            projectionsIconFill={projectionsIconFill}
            combinedFirstLineExtras={undefined}
            trueIqAppendSeries={[]}
            chartGridTop="52"
            trueIqChartMode={trueIqShadingOn}
          />
        </AccordionTab>

        <AccordionTab
          headerTemplate={accordionHeader("Interest Credits")}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          <InterestCreditsPanel
            isDark={isDark}
            cardBg={cardBg}
            divider={divider}
            graphData={graphData as any}
            tableData={tableData}
          />
        </AccordionTab>

        <AccordionTab
          headerTemplate={accordionHeader("Performance Table")}
          headerClassName="interactive-proposal-projection"
          className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
        >
          <PerformanceTablePanel
            isDark={isDark}
            cardBg={cardBg}
            textSub={textSub}
            chartBusy={chartBusy}
            columns={tableColumns}
            rows={tableData}
            rowClassName={tableRowClassName}
          />
        </AccordionTab>
      </Accordion>
    </div>
  );
}

export default Performance;
